/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { ChatbotApiService } from './chatbot-api.service';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { ChatbotDto, ChatbotParamsDto, PaginatedChatbotsDto } from '../../models/chatbot/chatbot.dto';
import { CreateChatbotDto } from '../../models/chatbot/create-chatbot.dto';
import { ChatbotStatus, ChatbotStatusLabel } from '../../../shared/constants/chatbot-status.constants';

@Injectable({
  providedIn: 'root'
})
export class ChatbotService {

  constructor(private chatbotApiService: ChatbotApiService) { }

  /**
   * Retrieves the current chatbot.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.chatbotService.checkIfHasAnyChatbot();
   *
   * // Use this in Interceptors, Guards and Functions
   * chatbotService.checkIfHasAnyChatbot();
   * ```
   *
   * @returns Observable<ChatbotDto> - Current chatbot
   */
  public checkIfHasAnyChatbot(): Observable<ChatbotDto> {
    const chatbotParams: ChatbotParamsDto = {
      PageNumber: 1,
      PageSize: 10
    }

    return this.chatbotApiService.getChatbotApi(chatbotParams).pipe(
      map((response: PaginatedChatbotsDto) => response?.chatbots?.[0] ?? null),
      switchMap((chatbotFounded: ChatbotDto | null) => {
        if (chatbotFounded) return of(chatbotFounded);

        const createChatbot: CreateChatbotDto = {
          status: ChatbotStatus.InTesting,
          allowToTechnician: false
        };
        return this.chatbotApiService.createChatbotApi(createChatbot);
      }),
      catchError(error => throwError(() => error))
    );
  }

  getCurrentModeChatbotStatus(): Observable<boolean> {
    return this.chatbotApiService.getCurrentModeChatbotStatusApi().pipe(
      map((status: any) => {
        if (status?.label === ChatbotStatusLabel.Live) {
          return false;
        }
        return true;
      }),
      catchError(error => throwError(() => error))
    );
  }
}
